import type { FC } from 'react'
import type { BulkUpdateRewardDTO, Reward, RewardCategoryWithRewards, CreateRewardCategoryDTO } from 'types/rewards'

import { DeleteOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import SimpleTitle from 'components/SimpleTitle'
import useAuth from 'hooks/useAuth'
import {
  useFetchCategoriesWithRewards,
  useCreateRewardCategory,
  useBulkUpdateRewards,
  invalidateRewardsCache,
} from 'hooks/useRewards'

import { useSnackbar } from 'notistack'
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ThemeMode } from 'types/config'
import { getRootOrganizationId } from 'types/user-profile'
import { CategorySelector } from '../components'

interface RewardEditMeta {
  id: string
  name: string
  categoryId: string
  displayOrder: number
  supportsEmployeeContribution: boolean
}

interface LocationState {
  rewards?: (Reward & { categoryName?: string })[]
}

const EditRewards: FC = () => {
  const theme = useTheme()
  const hoverBackgroundColor =
    theme.palette.mode === ThemeMode.DARK ? theme.palette.primary.darker : theme.palette.secondary.dark

  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const location = useLocation()

  const { token, profile } = useAuth()
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')

  // Get rewards from navigation state
  const locationState = location.state as LocationState | null
  const passedRewards = locationState?.rewards || []

  const [rewardMetas, setRewardMetas] = useState<RewardEditMeta[]>([])
  const [isReviewMode, setIsReviewMode] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [reviewRewards, setReviewRewards] = useState<BulkUpdateRewardDTO[]>([])
  const [isInitialized, setIsInitialized] = useState(false)

  // Fetch categories
  const {
    categoriesWithRewards,
    isLoading: isLoadingCategories,
    refetch: refetchCategories,
  } = useFetchCategoriesWithRewards(rootOrganizationId, token!, !!rootOrganizationId && !!token)

  const { createCategory } = useCreateRewardCategory(token!)
  const { bulkUpdateRewards, isLoading: isUpdatingRewards } = useBulkUpdateRewards(token!)

  // Build category map for lookup
  const categoryMap = useMemo(() => {
    if (!categoriesWithRewards) return {}
    return categoriesWithRewards.reduce(
      (acc, cat) => {
        acc[cat.id!] = cat
        return acc
      },
      {} as Record<string, RewardCategoryWithRewards>
    )
  }, [categoriesWithRewards])

  useEffect(() => {
    if (profile) {
      const orgId = getRootOrganizationId(profile)
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile])

  // Initialize reward metas from passed rewards
  useEffect(() => {
    if (passedRewards.length > 0 && !isInitialized) {
      const metas: RewardEditMeta[] = passedRewards.map((reward) => ({
        id: reward.id!,
        name: reward.name,
        categoryId: reward.categoryId,
        displayOrder: reward.displayOrder,
        supportsEmployeeContribution: reward.supportsEmployeeContribution,
      }))
      setRewardMetas(metas)
      setIsInitialized(true)
    }
  }, [passedRewards, isInitialized])

  const handleRewardChange = (index: number, field: keyof RewardEditMeta, value: any) => {
    const newMetas = [...rewardMetas]
    newMetas[index] = { ...newMetas[index], [field]: value }
    setRewardMetas(newMetas)
  }

  const removeReward = (index: number) => {
    setRewardMetas((prev) => prev.filter((_, i) => i !== index))
  }

  const createReviewRewards = useCallback(() => {
    const rewards: BulkUpdateRewardDTO[] = rewardMetas
      .filter((meta) => meta.name.trim() !== '' && meta.categoryId !== '')
      .map((meta) => ({
        id: meta.id,
        name: meta.name,
        categoryId: meta.categoryId,
        displayOrder: meta.displayOrder,
        supportsEmployeeContribution: meta.supportsEmployeeContribution,
      }))

    setReviewRewards(rewards)
    setIsReviewMode(true)
  }, [rewardMetas])

  const handleReview = () => {
    createReviewRewards()
  }

  const handleUpdateRewards = async () => {
    setIsSubmitting(true)
    try {
      const result = await bulkUpdateRewards(reviewRewards)

      enqueueSnackbar(`${result.count} rewards updated successfully`, { variant: 'success' })

      // Reset form
      setIsReviewMode(false)
      invalidateRewardsCache(rootOrganizationId, token!)

      startTransition(() => navigate('/rewards/manage'))
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update rewards'
      enqueueSnackbar(errorMessage, { variant: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBackFromReview = () => {
    setIsReviewMode(false)
  }

  // Category creation handler for inline creation
  const handleCreateCategory = useCallback(
    async (dto: CreateRewardCategoryDTO) => {
      try {
        await createCategory(dto)
        enqueueSnackbar('Category created successfully', { variant: 'success' })
        invalidateRewardsCache(rootOrganizationId, token!)
        await refetchCategories()
      } catch (error) {
        enqueueSnackbar('Failed to create category', { variant: 'error' })
        throw error
      }
    },
    [createCategory, enqueueSnackbar, rootOrganizationId, token, refetchCategories]
  )

  const renderRewardsConfig = () => (
    <Paper sx={{ p: 3, mb: 2 }}>
      {isLoadingCategories ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {rewardMetas.map((rewardMeta, index) => (
            <Grid container size={{ xs: 12 }} spacing={2} key={`reward-meta-${rewardMeta.id}`}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <CategorySelector
                  value={rewardMeta.categoryId}
                  onChange={(value) => handleRewardChange(index, 'categoryId', value)}
                  categories={categoriesWithRewards}
                  isLoading={isLoadingCategories}
                  onCreateCategory={handleCreateCategory}
                  organizationId={rootOrganizationId}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Reward Name *"
                  value={rewardMeta.name}
                  onChange={(e) => handleRewardChange(index, 'name', e.target.value)}
                  variant="outlined"
                  pill
                  shrink
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={rewardMeta.supportsEmployeeContribution}
                      onChange={(e) => handleRewardChange(index, 'supportsEmployeeContribution', e.target.checked)}
                    />
                  }
                  label="Employee Contribution"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 1 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                    Order
                  </Typography>
                  <Typography variant="body1">{rewardMeta.displayOrder}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 1 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <IconButton onClick={() => removeReward(index)} disabled={rewardMetas.length === 1}>
                  <DeleteOutlined />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          {rewardMetas.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Box textAlign="center" py={4}>
                <Typography color="textSecondary">No rewards to edit</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </Paper>
  )

  const renderReviewConfig = () => (
    <>
      <Typography variant="h4" gutterBottom>
        Review Changes
      </Typography>
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table stickyHeader size="small" aria-label="review-rewards-table">
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                <TableCell width="30%">Category</TableCell>
                <TableCell width="35%">Name</TableCell>
                <TableCell width="10%" align="center">
                  Order
                </TableCell>
                <TableCell width="25%" align="center">
                  Employee Contribution
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviewRewards.map((reward) => (
                <TableRow key={reward.id} sx={{ py: 3, '&:hover': { backgroundColor: hoverBackgroundColor } }}>
                  <TableCell>{categoryMap[reward.categoryId!]?.name || reward.categoryId}</TableCell>
                  <TableCell>{reward.name}</TableCell>
                  <TableCell align="center">{reward.displayOrder}</TableCell>
                  <TableCell align="center">{reward.supportsEmployeeContribution ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))}
              {reviewRewards.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="textSecondary">No rewards to update</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  )

  // Show error if no rewards passed
  if (passedRewards.length === 0) {
    return (
      <>
        <Grid container pb={2}>
          <Grid size={{ xs: 12 }}>
            <SimpleTitle title="Edit Rewards" />
          </Grid>
        </Grid>
        <Paper sx={{ p: 3 }}>
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">No rewards selected for editing</Typography>
            <Button
              variant="contained"
              onClick={() => startTransition(() => navigate('/rewards/manage'))}
              sx={{ mt: 2 }}
            >
              Back to Rewards
            </Button>
          </Box>
        </Paper>
      </>
    )
  }

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 12 }}>
          <SimpleTitle title="Edit Rewards" />
        </Grid>
      </Grid>

      {!isReviewMode && (
        <>
          {renderRewardsConfig()}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <AnimateButton>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    startTransition(() => {
                      navigate('/rewards/manage')
                    })
                  }}
                >
                  Cancel
                </Button>
              </AnimateButton>
              <AnimateButton>
                <Button
                  id="review-rewards-button"
                  variant="contained"
                  onClick={handleReview}
                  disabled={isLoadingCategories || rewardMetas.length === 0}
                >
                  Review
                </Button>
              </AnimateButton>
            </Stack>
          </Box>
        </>
      )}

      {isReviewMode && (
        <>
          {renderReviewConfig()}
          <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2}>
              <AnimateButton>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    startTransition(() => {
                      navigate('/rewards/manage')
                    })
                  }}
                >
                  Cancel
                </Button>
              </AnimateButton>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleBackFromReview}>
                Back
              </Button>
              <Button
                id="update-rewards-button"
                variant="contained"
                onClick={handleUpdateRewards}
                disabled={isSubmitting || isUpdatingRewards || reviewRewards.length === 0}
              >
                {isSubmitting || isUpdatingRewards ? <CircularProgress size={24} color="inherit" /> : 'Update Rewards'}
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </>
  )
}

export default EditRewards
